import { useLazyQuery } from '@apollo/client';
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  type AutocompleteProps,
} from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useSettingIds } from '~/components/settings';
import { useAppContext } from '~/contexts';
import type { ChannelAutocomplete__Channel as Channel } from './ChannelAutocomplete.generated';
import { ChannelAutocompleteDocument } from './ChannelAutocomplete.generated';

export interface ChannelAutocompleteProps
  extends Omit<
    AutocompleteProps<Channel | null, false, false, false>,
    'options' | 'onChange' | 'renderInput' | 'defaultValue' | 'freeSolo' | 'multiple'
  > {
  channelGuideId?: number;
  onChange: (channel: Channel | null) => void;
  value: Channel | null;
}

export const ChannelAutocomplete = ({
  channelGuideId,
  onChange,
  value,
  ...props
}: ChannelAutocompleteProps) => {
  const { t } = useTranslation();
  // Inside a settings row the row's label names the input.
  const ids = useSettingIds();
  const { currentNetwork } = useAppContext();
  const [open, setOpen] = useState(false);

  const [query, { called, data, loading }] = useLazyQuery(ChannelAutocompleteDocument);

  const handleOpen = useCallback(() => {
    setOpen(true);
    if (!called) {
      void query({ variables: { networkId: currentNetwork.id, channelGuideId } });
    }
  }, [called, channelGuideId, currentNetwork.id, query]);

  const handleClose = useCallback(() => setOpen(false), []);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent<Element, Event>, newValue: Channel | null) => {
      onChange(newValue);
    },
    [onChange],
  );

  return (
    <Autocomplete
      id={ids?.inputId}
      {...props}
      fullWidth
      autoHighlight
      getOptionLabel={(option) =>
        `${option?.name ?? ''}${option?.number ? ` (${option.number})` : ''}`
      }
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      loading={loading}
      onChange={handleChange}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      options={data?.network?.allChannels ?? []}
      renderInput={(params) => (
        <TextField
          {...namedByPlaceholder(params, t('common.placeholders.select_channel'), ids != null)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    aria-label={t('common.status.loading')}
                    color="inherit"
                    size={20}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(optionProps, option) =>
        option ? (
          <Box component="li" {...optionProps}>
            <Box
              sx={{ backgroundColor: option.backgroundColor, mr: 1, '& > img': { flexShrink: 0 } }}
            >
              <img
                loading="lazy"
                width="20"
                srcSet={option.thumbnailFile.uri}
                src={option.thumbnailFile.uri}
                alt=""
              />
            </Box>
            {option.name}
            {option.number ? ` (${option.number})` : ''}
          </Box>
        ) : null
      }
      value={value}
    />
  );
};
